/**
 * Revenue Flow Deal Card
 * 
 * Minimal deal card showing only:
 * - Name
 * - € Value
 * - Momentum (↑ ↓ →)
 * - Next Action (Call / WhatsApp / Send to phone)
 * - Risk badge (Time / Inactivity)
 */

import { Deal } from '@/hooks/useDeals';
import { Lead } from '@/hooks/useLeads';
import { formatCurrency } from '@/hooks/useUnifiedMetrics';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Phone,
  MessageCircle,
  Smartphone,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Clock,
  Trophy,
  XCircle,
  Calendar,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { computeMomentum, computeCloseProbability, MomentumStatus } from '@/lib/dealMetrics';
import { buildTelLink, buildWhatsAppLink } from '@/core/decision-engine/eliteEngine';
import { differenceInDays, differenceInHours } from 'date-fns';

interface RevenueFlowDealCardProps {
  deal: Deal;
  lead?: Lead;
  onCall?: (deal: Deal, lead?: Lead) => void;
  onWhatsApp?: (deal: Deal, lead?: Lead) => void;
  onSendToPhone?: (deal: Deal, lead?: Lead, actionType: 'call' | 'whatsapp') => void;
  onCardClick?: (deal: Deal) => void;
  onMarkWon?: (deal: Deal) => void;
  onMarkLost?: (deal: Deal) => void;
  onReschedule?: (deal: Deal, lead?: Lead) => void;
}

export function RevenueFlowDealCard({
  deal,
  lead,
  onCall,
  onWhatsApp,
  onSendToPhone,
  onCardClick,
  onMarkWon,
  onMarkLost,
  onReschedule,
}: RevenueFlowDealCardProps) {
  const now = new Date();
  const dealValue = deal.value || 0;
  
  // Compute momentum
  const momentum = computeMomentum(deal, lead);
  const momentumStatus = momentum.status;
  
  // Compute risk badges
  const riskBadges: string[] = [];
  let isAtRisk = false;
  
  // Check for overdue follow-up
  const nextFollowUp = (lead as any)?.next_follow_up_at ? new Date((lead as any).next_follow_up_at) : null;
  if (nextFollowUp && nextFollowUp < now) {
    const daysOverdue = Math.floor(differenceInDays(now, nextFollowUp));
    riskBadges.push(`${daysOverdue}d overdue`);
    isAtRisk = true;
  }
  
  // Check for SLA violation
  const lastContact = lead?.last_interaction_at ? new Date(lead.last_interaction_at) : null;
  if (!lastContact) {
    riskBadges.push('Never contacted');
    isAtRisk = true;
  } else {
    const hoursSinceContact = differenceInHours(now, lastContact);
    const slaHours = lead?.temperature === 'hot' ? 6 : lead?.temperature === 'warm' ? 24 : 72;
    if (hoursSinceContact > slaHours) {
      const daysSince = Math.floor(hoursSinceContact / 24);
      riskBadges.push(`${daysSince}d inactive`);
      isAtRisk = true;
    }
  }
  
  // Check for stalling
  if (deal.updated_at) {
    const daysSinceUpdate = differenceInDays(now, new Date(deal.updated_at));
    if (daysSinceUpdate >= 14) {
      riskBadges.push('Stalled');
      isAtRisk = true;
    }
  }
  
  // Contact links
  const hasPhone = Boolean(lead?.phone);
  const telLink = hasPhone ? buildTelLink(lead?.phone || '') : null;
  const whatsappLink = hasPhone ? buildWhatsAppLink(lead?.phone || '') : null;
  
  // Momentum icon
  const MomentumIcon = momentumStatus === 'at_risk' 
    ? TrendingDown 
    : momentumStatus === 'slowing' 
    ? Minus 
    : TrendingUp;
  
  const momentumColor = momentumStatus === 'at_risk'
    ? 'text-destructive'
    : momentumStatus === 'slowing'
    ? 'text-warning'
    : 'text-success';
  
  const momentumLabel = momentumStatus === 'at_risk'
    ? 'At risk'
    : momentumStatus === 'slowing'
    ? 'Slowing'
    : 'Stable';

  return (
    <Card
      className={cn(
        "group rounded-lg border bg-card transition-all duration-200 cursor-pointer",
        "hover:shadow-sm hover:border-primary/30",
        isAtRisk && "border-destructive/30 bg-destructive/5"
      )}
      onClick={() => onCardClick?.(deal)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Main content */}
          <div className="flex-1 min-w-0 space-y-2">
            {/* Name */}
            <h4 className="font-semibold text-foreground text-base">
              {lead?.name || deal.lead?.name || 'Unknown Lead'}
            </h4>
            
            {/* Value */}
            {dealValue > 0 && (
              <div className="text-lg font-bold text-primary">
                {formatCurrency(dealValue)}
              </div>
            )}
            
            {/* Momentum */}
            <div className="flex items-center gap-2">
              <MomentumIcon className={cn("w-4 h-4", momentumColor)} />
              <span className={cn("text-xs font-medium", momentumColor)}>
                {momentumLabel}
              </span>
            </div>
            
            {/* Risk badges */}
            {riskBadges.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap">
                {riskBadges.map((badge, idx) => (
                  <Badge
                    key={idx}
                    variant="outline"
                    className={cn(
                      "text-[10px] px-1.5 py-0",
                      isAtRisk && "border-destructive/30 text-destructive"
                    )}
                  >
                    {badge}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          
          {/* Actions */}
          <div
            className="flex items-center gap-1.5 flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Contact actions */}
            {(telLink || whatsappLink) && (
              <>
                {onSendToPhone && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onSendToPhone(deal, lead, telLink ? 'call' : 'whatsapp')}
                    className="gap-1.5 h-8 px-2.5"
                    title="Send to phone"
                  >
                    <Smartphone className="w-3.5 h-3.5" />
                  </Button>
                )}
                
                {telLink && onCall && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onCall(deal, lead)}
                    className="gap-1.5 h-8 px-2.5"
                    title="Call"
                  >
                    <Phone className="w-3.5 h-3.5" />
                  </Button>
                )}
                
                {whatsappLink && onWhatsApp && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onWhatsApp(deal, lead)}
                    className="gap-1.5 h-8 px-2.5"
                    title="WhatsApp"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                  </Button>
                )}
              </>
            )}
            
            {/* Deal actions dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <span className="sr-only">More actions</span>
                  <span className="text-xs">⋯</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {onMarkWon && (
                  <DropdownMenuItem onClick={() => onMarkWon(deal)}>
                    <Trophy className="w-4 h-4 mr-2 text-success" />
                    Mark as Won
                  </DropdownMenuItem>
                )}
                {onMarkLost && (
                  <DropdownMenuItem onClick={() => onMarkLost(deal)}>
                    <XCircle className="w-4 h-4 mr-2 text-destructive" />
                    Mark as Lost
                  </DropdownMenuItem>
                )}
                {(onMarkWon || onMarkLost) && onReschedule && <DropdownMenuSeparator />}
                {onReschedule && (
                  <DropdownMenuItem onClick={() => onReschedule(deal, lead)}>
                    <Calendar className="w-4 h-4 mr-2" />
                    Reschedule
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

